// FirewallGroups.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Search, Users,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, X, Link2, Hash, Server, Tag
} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";
import { ItemsPerPageDropdown } from "../../../../../components/ItemsPerPageDropdown ";


// ── Types ─────────────────────────────────────────────────────────────────────
interface MemberItem {
    name: string;
    q_origin_key: string;
}

interface MatchItem {
    id: number;
    "group-name": string;
    "server-name": string;
    q_origin_key: number;
}

interface FirewallGroupItem {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    firewall_type: string;
    name: string;
    group_type: string;
    authtimeout: number;
    http_digest_realm: string;
    sso_attribute_value: string;
    max_accounts: number;
    member: string;
    match: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseJSON = <T,>(raw: any): T[] => {
    if (typeof raw === "object" && raw !== null) {
        if (Array.isArray(raw)) return raw as T[];
        return [raw] as T[];
    }
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        return parsed ? [parsed] : [];
    } catch { return []; }
};

// ── Group Type Badge ──────────────────────────────────────────────────────────
const GROUP_TYPE_STYLES: Record<string, string> = {
    "firewall": "bg-blue-500/10 border-blue-500/20 text-blue-400",
    "fsso-service": "bg-purple-500/10 border-purple-500/20 text-purple-400",
    "guest": "bg-green-500/10 border-green-500/20 text-green-400",
};

const GroupTypeBadge = ({ type }: { type: string }) => (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase whitespace-nowrap ${GROUP_TYPE_STYLES[type] ?? "bg-gray-500/10 border-gray-500/20 text-gray-400"}`}>
        {type}
    </span>
);

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ column, sortConfig }: {
    column: string;
    sortConfig: { key: string; direction: "asc" | "desc" } | null;
}) => {
    if (sortConfig?.key !== column) return (
        <div className="opacity-0 group-hover:opacity-40 transition-opacity">
            <ArrowUp size={10} className="text-slate-400" />
        </div>
    );
    return sortConfig.direction === "asc"
        ? <ArrowUp size={10} className="text-blue-400" />
        : <ArrowDown size={10} className="text-blue-400" />;
};

// ── Right Drawer ──────────────────────────────────────────────────────────────
function GroupDrawer({ item, onClose }: { item: FirewallGroupItem; onClose: () => void }) {
    const members = parseJSON<MemberItem>(item.member);
    const matches = parseJSON<MatchItem>(item.match);

    // Extract CN name from DN string
    const extractCN = (dn: string): string => {
        const match = dn.match(/^CN=([^,]+)/i);
        return match ? match[1] : dn;
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 z-40 transition-opacity"
                onClick={onClose}
            />

            {/* Drawer */}
            <div className="fixed top-0 right-0 h-full w-[30%] min-w-[320px] bg-gray-900 border-l border-gray-700 z-55 flex flex-col shadow-2xl animate-slide-in-right overflow-hidden">

                {/* Drawer Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-800/60 shrink-0">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                            <Users size={13} className="text-blue-400" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[12px] font-black text-white truncate">{item.name}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <GroupTypeBadge type={item.group_type} />
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-700 transition-all shrink-0 ml-2"
                    >
                        <X size={14} />
                    </button>
                </div>

                {/* Drawer Body */}
                <div className="flex-1 overflow-y-auto custom-scroll p-4 space-y-5">

                    {/* Members */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-5 h-5 rounded bg-cyan-600/20 border border-cyan-500/30 flex items-center justify-center">
                                <Server size={10} className="text-cyan-400" />
                            </div>
                            <h3 className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">
                                Members
                            </h3>
                            <span className="px-1.5 py-0.5 text-[8px] font-bold bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 rounded">
                                {members.length}
                            </span>
                        </div>

                        {members.length === 0 ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
                                <span className="text-[10px] text-gray-500">No members configured</span>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {members.map((m, i) => (
                                    <div key={i} className="flex items-center gap-2.5 p-2.5 bg-gray-800/50 border border-gray-700/60 rounded-lg hover:border-cyan-500/20 transition-colors">
                                        <div className="w-6 h-6 rounded-full bg-cyan-600/20 border border-cyan-500/20 flex items-center justify-center shrink-0">
                                            <Server size={10} className="text-cyan-400" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[11px] font-bold text-gray-100">{m.name}</p>
                                            {m.q_origin_key !== m.name && (
                                                <p className="text-[9px] text-gray-500 truncate">{m.q_origin_key}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Matches */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-5 h-5 rounded bg-purple-600/20 border border-purple-500/30 flex items-center justify-center">
                                <Link2 size={10} className="text-purple-400" />
                            </div>
                            <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest">
                                Match Rules
                            </h3>
                            <span className="px-1.5 py-0.5 text-[8px] font-bold bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded">
                                {matches.length}
                            </span>
                        </div>

                        {matches.length === 0 ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg">
                                <span className="text-[10px] text-gray-500">No match rules configured</span>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {matches.map((m, i) => {
                                    const groupName = m["group-name"];
                                    const isGuid = /^[0-9a-f-]{36}$/i.test(groupName);
                                    const isDN = groupName.startsWith("CN=");
                                    const displayName = isDN ? extractCN(groupName) : groupName;

                                    return (
                                        <div key={i} className="p-3 bg-gray-800/50 border border-gray-700/60 rounded-lg hover:border-purple-500/20 transition-colors space-y-2">
                                            {/* Rule ID + Server */}
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-1.5">
                                                    <Hash size={9} className="text-gray-500" />
                                                    <span className="text-[9px] font-bold text-gray-500">Rule {m.id}</span>
                                                </div>
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-gray-700/50 border-gray-600/40 text-gray-300">
                                                    <Server size={8} />
                                                    {m["server-name"]}
                                                </span>
                                            </div>

                                            {/* Group Name */}
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1">Group</p>
                                                {isDN ? (
                                                    <div>
                                                        <p className="text-[11px] font-bold text-purple-300">{displayName}</p>
                                                        <p className="text-[8px] text-gray-500 mt-0.5 break-all font-mono leading-relaxed">{groupName}</p>
                                                    </div>
                                                ) : isGuid ? (
                                                    <div>
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Azure Object ID</p>
                                                        <p className="font-mono text-[9px] text-purple-300 break-all">{groupName}</p>
                                                    </div>
                                                ) : (
                                                    <p className="text-[11px] font-bold text-purple-300">{groupName}</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Meta */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-5 h-5 rounded bg-gray-600/30 border border-gray-600/40 flex items-center justify-center">
                                <Tag size={10} className="text-gray-400" />
                            </div>
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Details</h3>
                        </div>
                        <div className="bg-gray-800/40 border border-gray-700/50 rounded-lg p-3 space-y-1">
                            {[
                                ["Group Type", item.group_type],
                                ["Auth Timeout", item.authtimeout ? `${item.authtimeout}s` : "Default"],
                                ["Max Accounts", item.max_accounts || "Unlimited"],
                                ["HTTP Digest Realm", item.http_digest_realm || "—"],
                                ["SSO Attribute", item.sso_attribute_value || "—"],
                            ].map(([label, value]) => (
                                <div key={label} className="flex justify-between items-center py-1 border-b border-gray-700/40 last:border-0">
                                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">{label}</span>
                                    <span className="text-[10px] font-bold text-gray-300">{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallGroups({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [items, setItems] = useState<FirewallGroupItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selected, setSelected] = useState<FirewallGroupItem | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) queryObj.$or = [
                { name: { $ilike: `%${debouncedSearch}%` } },
                { group_type: { $ilike: `%${debouncedSearch}%` } },
            ];
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit,
            };
            if (sortConfig) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.direction; }
            const res = await baseapi.get("/firewall_groups", { params });
            if (res.data.status) {
                setItems(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else showError("Failed to load firewall groups");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, debouncedSearch, currentPage, limit, sortConfig]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSort = (key: string) => {
        setSortConfig(prev =>
            prev?.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    const Th = ({ label, col, right = false }: { label: string; col: string; right?: boolean }) => (
        <th
            className={`px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}
            onClick={() => handleSort(col)}
        >
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label} <SortIcon column={col} sortConfig={sortConfig} />
            </div>
        </th>
    );

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {/* Drawer */}
            {selected && <GroupDrawer item={selected} onClose={() => setSelected(null)} />}

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-600/20 p-1.5 rounded-lg border border-blue-500/30">
                        <Users size={16} className="text-blue-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Firewall Groups</h1>
                    <span className="text-[10px] font-bold text-gray-500 bg-gray-800 border border-gray-700 px-2 py-0.5 rounded">
                        {totalRecords} groups
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search groups..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-blue-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <Th label="Name" col="name" />
                                <Th label="Group Type" col="group_type" />
                                <Th label="Members" col="member" />
                                <Th label="Match Rules" col="match" />
                                <Th label="Auth Timeout" col="authtimeout" right />
                                <Th label="Max Accounts" col="max_accounts" right />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading groups...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <Users size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">No groups found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((item) => {
                                    const members = parseJSON<MemberItem>(item.member);
                                    const matches = parseJSON<MatchItem>(item.match);

                                    return (
                                        <tr
                                            key={item.id}
                                            onClick={() => setSelected(item)}
                                            className="hover:bg-gray-700/50 transition-colors cursor-pointer"
                                            title="Click to view members & match rules"
                                        >
                                            {/* Name */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <div className="flex items-center gap-2">

                                                    <span className="text-[11px] font-bold text-gray-100">{item.name.replace(/_/g, " ")}</span>
                                                </div>
                                            </td>

                                            {/* Group Type */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <GroupTypeBadge type={item.group_type} />
                                            </td>

                                            {/* Members */}
                                            <td className="px-4 py-2.5">
                                                {members.length === 0 ? (
                                                    <span className="text-gray-600 text-[10px]">—</span>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1">
                                                        {members.map((m, i) => (
                                                            <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-cyan-500/10 border-cyan-500/20 text-cyan-300">
                                                                <Server size={8} />
                                                                {m.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Match Rules */}
                                            <td className="px-4 py-2.5">
                                                {matches.length === 0 ? (
                                                    <span className="text-gray-600 text-[10px]">—</span>
                                                ) : (
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-purple-500/10 border-purple-500/20 text-purple-300">
                                                            <Link2 size={8} />
                                                            {matches.length} rule{matches.length > 1 ? "s" : ""}
                                                        </span>

                                                    </div>
                                                )}
                                            </td>

                                            {/* Auth Timeout */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className="font-mono text-[10px] text-gray-400">
                                                    {item.authtimeout ? `${item.authtimeout}s` : "Default"}
                                                </span>
                                            </td>

                                            {/* Max Accounts */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className="font-mono text-[10px] text-gray-400">
                                                    {item.max_accounts || "∞"}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-600">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
